import { z } from 'zod';
import {
  ActiveSessionSchema, ErrorDetailSchema, ErrorEventSchema, ErrorsPageSchema, IssueStateSchema,
  PerformanceBucketSchema, ProjectSchema, ReleaseSchema, SessionDetailSchema,
  SessionEventsPageSchema, SlowRowSchema, StatsBucketSchema, SymbolicateFrameSchema,
  SystemStatsSchema, UsersPageSchema,
  type Project,
} from './types';

export class HttpError extends Error {
  constructor(public status: number, msg: string) { super(msg); }
}

async function fetchJson<T>(path: string, schema: z.ZodType<T>, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  if (!res.ok) throw new HttpError(res.status, await res.text().catch(() => res.statusText));
  const body = await res.json();
  return schema.parse(body);
}

export const api = {
  login(email: string, password: string) {
    return fetch('/api/auth/login', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    }).then((r) => { if (!r.ok) throw new HttpError(r.status, 'login failed'); });
  },

  logout() {
    return fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
  },

  listProjects() {
    return fetchJson('/api/projects', z.array(ProjectSchema));
  },

  createProject(name: string, allowedOrigins: string[] = []): Promise<Project> {
    return fetchJson('/api/projects', ProjectSchema, {
      method: 'POST',
      body: JSON.stringify({ name, allowed_origins: allowedOrigins }),
    });
  },

  patchProject(id: string, body: { name?: string; allowed_origins?: string[]; ip_truncate?: boolean }) {
    return fetch(`/api/projects/${id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => { if (!r.ok) throw new HttpError(r.status, 'patch failed'); });
  },

  wipeProject(id: string) {
    return fetch(`/api/projects/${id}/wipe`, {
      method: 'POST',
      credentials: 'include',
    }).then((r) => { if (!r.ok) throw new HttpError(r.status, 'wipe failed'); });
  },

  deleteProject(id: string) {
    return fetch(`/api/projects/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    }).then((r) => { if (!r.ok) throw new HttpError(r.status, 'delete failed'); });
  },

  listErrors(projectId: string, params: Record<string, string> = {}) {
    const qs = new URLSearchParams(params).toString();
    return fetchJson(`/api/projects/${projectId}/errors${qs ? '?' + qs : ''}`, ErrorsPageSchema);
  },

  errorDetail(projectId: string, fingerprint: string) {
    return fetchJson(`/api/projects/${projectId}/errors/${fingerprint}`, ErrorDetailSchema);
  },

  event(projectId: string, eventId: string) {
    return fetchJson(`/api/projects/${projectId}/events/${eventId}`, ErrorEventSchema);
  },

  stats(projectId: string, groupBy: 'day' | 'hour' = 'day', uiVersion?: string) {
    const params = new URLSearchParams({ group_by: groupBy });
    if (uiVersion) params.set('ui_version', uiVersion);
    return fetchJson(`/api/projects/${projectId}/stats?${params}`, z.array(StatsBucketSchema));
  },

  listReleases(projectId: string) {
    return fetchJson(`/api/projects/${projectId}/releases`, z.array(ReleaseSchema));
  },

  listUsers(projectId: string, within: string = '24h', q: string = '', cursor?: string, limit?: number) {
    const params = new URLSearchParams({ within });
    if (q) params.set('q', q);
    if (cursor) params.set('cursor', cursor);
    if (limit)  params.set('limit', String(limit));
    return fetchJson(`/api/projects/${projectId}/users?${params}`, UsersPageSchema);
  },

  sessionDetail(projectId: string, sessionId: string) {
    return fetchJson(`/api/projects/${projectId}/sessions/${sessionId}`, SessionDetailSchema);
  },

  sessionEvents(projectId: string, sessionId: string, cursor?: string, limit?: number) {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    if (limit)  params.set('limit', String(limit));
    const qs = params.toString();
    return fetchJson(
      `/api/projects/${projectId}/sessions/${sessionId}/errors${qs ? '?' + qs : ''}`,
      SessionEventsPageSchema,
    );
  },

  activeSessions(projectId: string, within: string = '5m') {
    return fetchJson(
      `/api/projects/${projectId}/sessions?within=${within}`,
      z.array(ActiveSessionSchema),
    );
  },

  issueState(projectId: string, fp: string) {
    return fetchJson(`/api/projects/${projectId}/issues/${fp}`, IssueStateSchema);
  },

  patchIssueState(projectId: string, fp: string, body: { state: 'open' | 'resolved' | 'ignored'; resolved_in?: string; note?: string }) {
    return fetch(`/api/projects/${projectId}/issues/${fp}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => { if (!r.ok) throw new HttpError(r.status, 'patch failed'); });
  },

  systemStats() {
    return fetchJson('/api/system/stats', SystemStatsSchema);
  },

  performance(projectId: string, within: string = '24h', uiVersion?: string) {
    const params = new URLSearchParams({ within });
    if (uiVersion) params.set('ui_version', uiVersion);
    return fetchJson(`/api/projects/${projectId}/performance?${params}`, z.array(PerformanceBucketSchema));
  },

  slow(projectId: string, within: string = '24h') {
    const params = new URLSearchParams({ within });
    return fetchJson(`/api/projects/${projectId}/slow?${params}`, z.array(SlowRowSchema));
  },

  symbolicate(projectId: string, version: string, stack: string) {
    const params = new URLSearchParams({ version });
    return fetchJson(`/api/projects/${projectId}/symbolicate?${params}`, z.array(SymbolicateFrameSchema), {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: stack,
    });
  },

  uploadSourceMap(projectId: string, version: string, file: File) {
    return fetch(`/api/projects/${projectId}/sourcemaps/${encodeURIComponent(version)}/${encodeURIComponent(file.name)}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: file,
    }).then((r) => { if (!r.ok) throw new HttpError(r.status, 'upload failed'); });
  },
};
